// Patient-facing SMS template catalog — the single source of wording for both
// the in-app preview and the actual server send.
//
// ⚠️ THIS FILE IS THE AUTHORED SOURCE. `functions/smsTemplates.js` is generated
// from it by `node scripts/syncSmsTemplates.js` (and `smsTemplates.sync.test.js`
// fails the build if the two drift). Edit here, never there.
//
// Why a generated mirror instead of a shared import: `functions/` is a separate
// CommonJS npm package, and CRA's ModuleScopePlugin hard-blocks any `src/`
// import that resolves outside `src/`. Preview/send drift — the therapist
// approving one wording and the patient receiving another — is the exact bug
// this catalog exists to prevent, so the mirror is machine-generated.
//
// Language: every entry has a `he` and an `en` variant. Hebrew is the default
// for every patient; English is only reached when the clinic opted in via
// `englishCommunicationEnabled` AND the patient is `communicationLanguage: "en"`.
// The catalog itself must stay dependency-free so the generated CJS copy can be
// required from Cloud Functions with no bundling.

export const MAX_SMS_LENGTH = 201;

// ~54 chars for ":\n\nhttps://clinikit.co.il/confirm/XXXXXXXXXXXXXXXXXXXX".
// Used as the length budget by callers that render a preview (no real link yet).
export const SMS_LINK_CHARS = 54;

export const DAY_NAMES = {
  he: ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
};

export const FREQUENCY_LABELS = {
  he: {
    weekly: "שבועי",
    biweekly: "אחת לשבועיים",
    monthly: "חודשי",
    twice_weekly: "פעמיים בשבוע",
  },
  en: {
    weekly: "weekly",
    biweekly: "every two weeks",
    monthly: "monthly",
    twice_weekly: "twice a week",
  },
};

/** Any unknown/absent language falls back to Hebrew. Never throws. */
export const normLang = (language) => (language === "en" ? "en" : "he");

/**
 * Smartly truncates a message to fit within the character limit, trying to
 * preserve a link at the end.
 *
 * Copied verbatim from functions/index.js, which is where this logic lived in
 * four near-identical copies (index.js, bookingHelper.js,
 * digitalSignatureHelper.js, questionnaireHelper.js). The exact behaviour —
 * including the `availableSpace > 20` guard and the single "\n" before the
 * link — is what real patients have been receiving, so it is preserved
 * byte-for-byte rather than tidied up.
 */
export const truncateMessage = (message, maxLength = MAX_SMS_LENGTH) => {
  if (message.length <= maxLength) {
    return message;
  }

  // Find the link in the message (assuming it's at the end)
  const linkMatch = message.match(/(https?:\/\/[^\s]+)$/);

  if (linkMatch) {
    const link = linkMatch[1];
    const linkIndex = message.lastIndexOf(link);
    const textBeforeLink = message.substring(0, linkIndex);

    // Calculate how much space we have for text (minus link and "...")
    const availableSpace = maxLength - link.length - 4; // 4 for "...\n"

    if (availableSpace > 20) {
      // Truncate the text before the link
      const truncatedText = textBeforeLink.substring(0, availableSpace).trim();
      return truncatedText + "...\n" + link;
    }
  }

  // Fallback: simple truncation with ellipsis
  return message.substring(0, maxLength - 3) + "...";
};

const freqLabel = (lang, frequency) =>
  FREQUENCY_LABELS[lang][frequency] || FREQUENCY_LABELS[lang].weekly;

/**
 * The catalog.
 *
 * Each entry exposes `he(vars, ctx)` / `en(vars, ctx)` returning the message
 * body WITHOUT the link, plus an optional `fallbacks` list of variable-strippers
 * applied in order while the body (plus the link budget) overflows the cap.
 *
 * `ctx.hasLink` is true when the caller will append a real link. It exists
 * because the preview and the send have always differed in their closing clause
 * (preview: "…בקישור המצורף." / send: "…בקישור המצורף:" followed by the URL) and
 * that difference is preserved byte-for-byte.
 */
export const TEMPLATES = {
  "appointment.initial": {
    // Only the treatment type is ever dropped — the clinic name stays even when
    // the message overflows, matching the wording that shipped before this
    // catalog existed. Anything past the cap falls through to truncateMessage.
    fallbacks: [(v) => ({ ...v, treatmentTypeName: "" })],
    he: ({ name, clinicName, treatmentTypeName, date, time }, { hasLink }) => {
      const t = treatmentTypeName ? ` ל${treatmentTypeName}` : "";
      const close = hasLink
        ? "אנא אשר/י הגעה בקישור המצורף:"
        : "אנא אשר/י הגעה בקישור המצורף.";
      return clinicName
        ? `היי ${name}, נקבעה לך פגישה${t} ב${clinicName} ב-${date} בשעה ${time}. ${close}`
        : `היי ${name}, נקבעה לך פגישה${t} ב-${date} בשעה ${time}. ${close}`;
    },
    en: ({ name, clinicName, treatmentTypeName, date, time }, { hasLink }) => {
      const t = treatmentTypeName ? ` for ${treatmentTypeName}` : "";
      const close = hasLink
        ? "Please confirm your attendance using the link below:"
        : "Please confirm your attendance using the link below.";
      return clinicName
        ? `Hi ${name}, an appointment${t} has been scheduled for you at ${clinicName} on ${date} at ${time}. ${close}`
        : `Hi ${name}, an appointment${t} has been scheduled for you on ${date} at ${time}. ${close}`;
    },
  },

  "appointment.reminder": {
    fallbacks: [(v) => ({ ...v, treatmentTypeName: "" })],
    he: ({ name, treatmentTypeName, date, time }, { hasLink }) => {
      const t = treatmentTypeName ? ` ל${treatmentTypeName}` : "";
      const close = hasLink ? "אנא אשר/י הגעה:" : "אנא אשר/י הגעה בקישור.";
      return `תזכורת: היי ${name}, יש לך פגישה${t} ב-${date} בשעה ${time}. ${close}`;
    },
    en: ({ name, treatmentTypeName, date, time }, { hasLink }) => {
      const t = treatmentTypeName ? ` for ${treatmentTypeName}` : "";
      const close = hasLink
        ? "Please confirm your attendance:"
        : "Please confirm your attendance using the link.";
      return `Reminder: Hi ${name}, you have an appointment${t} on ${date} at ${time}. ${close}`;
    },
  },

  "appointment.recurringReminder": {
    fallbacks: [(v) => ({ ...v, treatmentTypeName: "" })],
    he: ({ name, treatmentTypeName, date, time }) => {
      const t = treatmentTypeName ? ` ל${treatmentTypeName}` : "";
      return `תזכורת: היי ${name}, יש לך פגישה${t} מחר (${date}) בשעה ${time}. אנא אשר/י הגעה:`;
    },
    en: ({ name, treatmentTypeName, date, time }) => {
      const t = treatmentTypeName ? ` for ${treatmentTypeName}` : "";
      return `Reminder: Hi ${name}, you have an appointment${t} tomorrow (${date}) at ${time}. Please confirm your attendance:`;
    },
  },

  "appointment.reschedule": {
    he: ({ name, date, time }) =>
      `עדכון: היי ${name}, הפגישה שלך עודכנה ל-${date} בשעה ${time}. אנא אשר/י הגעה:`,
    en: ({ name, date, time }) =>
      `Update: Hi ${name}, your appointment has been moved to ${date} at ${time}. Please confirm your attendance:`,
  },

  // Recurring-series announcement. Built client-side and passed to the server
  // as `customMessage`, so the language decision happens in the UI.
  "appointment.series": {
    he: (
      { name, count, startDate, frequency, baseDayIndex, baseTime, secondDayIndex, secondTime },
      { hasLink },
    ) => {
      const days = DAY_NAMES.he;
      const slots =
        frequency === "twice_weekly" && secondDayIndex !== "" && secondDayIndex != null && secondTime
          ? `בימי ${days[baseDayIndex]} ב-${baseTime} ובימי ${days[Number(secondDayIndex)]} ב-${secondTime}`
          : `בימי ${days[baseDayIndex]} בשעה ${baseTime}`;
      const close = hasLink
        ? "אנא אשר/י קבלה בקישור:"
        : "אנא אשר/י קבלה בקישור המצורף.";
      return `היי ${name}, תואמה לך סדרת פגישות ${freqLabel("he", frequency)} - ${count} פגישות ${slots}, החל מ-${startDate}. ${close}`;
    },
    en: (
      { name, count, startDate, frequency, baseDayIndex, baseTime, secondDayIndex, secondTime },
      { hasLink },
    ) => {
      const days = DAY_NAMES.en;
      const slots =
        frequency === "twice_weekly" && secondDayIndex !== "" && secondDayIndex != null && secondTime
          ? `on ${days[baseDayIndex]}s at ${baseTime} and ${days[Number(secondDayIndex)]}s at ${secondTime}`
          : `on ${days[baseDayIndex]}s at ${baseTime}`;
      const close = hasLink
        ? "Please confirm using the link:"
        : "Please confirm using the link below.";
      return `Hi ${name}, a ${freqLabel("en", frequency)} series of ${count} appointments has been scheduled for you ${slots}, starting ${startDate}. ${close}`;
    },
  },

  // Video-meeting join link. Appended AFTER truncation so it always survives
  // intact — callers pass it as `suffix`, not as part of the body.
  "appointment.videoLinkSuffix": {
    he: ({ videoMeetLink }) => `\n\nקישור לפגישת הווידאו:\n${videoMeetLink}`,
    en: ({ videoMeetLink }) => `\n\nVideo meeting link:\n${videoMeetLink}`,
  },

  // Standalone video-link SMS (no confirmation link), sent as `customMessage`.
  "appointment.videoLinkMessage": {
    he: ({ name, date, time }) =>
      `היי ${name}, נקבעה לך פגישת וידאו אונליין ב-${date} בשעה ${time}. קישור להצטרפות לשיחה:`,
    en: ({ name, date, time }) =>
      `Hi ${name}, an online video appointment has been scheduled for you on ${date} at ${time}. Here is the link to join:`,
  },

  "appointment.seriesVideoLinkMessage": {
    he: ({ name, date, time }) =>
      `היי ${name}, נקבעה לך סדרת פגישות וידאו אונליין. קישור להצטרפות למפגש הראשון ב-${date} בשעה ${time}:`,
    en: ({ name, date, time }) =>
      `Hi ${name}, a series of online video appointments has been scheduled for you. Here is the link to join the first session on ${date} at ${time}:`,
  },

  // Online-booking decision (bookingHelper.notifyBookingDecisionImpl) — the
  // wording the booker has always received, now in the catalog so the router's
  // SMS fallback and the WhatsApp twin stay identical.
  "booking.approved": {
    he: ({ name, date, time }) => `היי ${name}, התור שלך ל-${date} בשעה ${time} אושר. נתראה!`,
    en: ({ name, date, time }) => `Hi ${name}, your appointment on ${date} at ${time} is confirmed. See you!`,
  },
  "booking.rejected": {
    he: ({ name, date }) => `היי ${name}, מצטערים, התור שביקשת ל-${date} לא אושר. ניתן ליצור קשר עם הקליניקה.`,
    en: ({ name, date }) => `Hi ${name}, unfortunately the appointment you requested for ${date} was not approved. Please contact the clinic.`,
  },

  // Website lead welcome — SMS twin of the WhatsApp LEAD_WELCOME template
  // (sent by ניב only when WhatsApp did not take the message).
  "lead.welcome": {
    he: ({ name }) =>
      `היי ${name}, אני ניב מ-CliniKit 😊 ראיתי שהשארת פרטים והתעניינת במערכת. אשמח שנקבע שיחה קצרה על הצרכים שלך. אפשר להשיב כאן או להתקשר.`,
    en: ({ name }) =>
      `Hi ${name}, this is Niv from CliniKit 😊 I saw you left your details. Happy to set up a short call about your clinic's needs. Reply here or give me a call.`,
  },

  // ---- Phase 4 automations (whatsapp_integration.md §3) — SMS twins of the
  // WhatsApp templates; vars mirror the WA buildParams.
  "appointment.cancelled": {
    he: ({ name, clinicName, date, time }) =>
      `היי ${name}, הפגישה שלך ב${clinicName} בתאריך ${date} בשעה ${time} בוטלה. לתיאום מחדש ניתן לפנות לקליניקה.`,
    en: ({ name, clinicName, date, time }) =>
      `Hi ${name}, your appointment at ${clinicName} on ${date} at ${time} was cancelled. Please contact the clinic to reschedule.`,
  },
  "booking.received": {
    he: ({ name, date, time, clinicName }) =>
      `היי ${name}, קיבלנו את בקשת התור ל-${date} בשעה ${time} ב${clinicName}. נעדכן ברגע שהתור יאושר.`,
    en: ({ name, date, time, clinicName }) =>
      `Hi ${name}, we received your booking request for ${date} at ${time} at ${clinicName}. We'll let you know once it's approved.`,
  },
  "appointment.unconfirmedNudge": {
    he: ({ name, date, time }) => `היי ${name}, טרם אישרת הגעה לפגישה ב-${date} בשעה ${time}. נשמח לאישור קצר:`,
    en: ({ name, date, time }) => `Hi ${name}, you haven't confirmed your appointment on ${date} at ${time} yet. A quick confirmation would help:`,
  },
  "appointment.noShowFollowup": {
    he: ({ name, clinicName }) => `היי ${name}, פספסנו אותך היום ב${clinicName}. נשמח לתאם מועד חדש.`,
    en: ({ name, clinicName }) => `Hi ${name}, we missed you today at ${clinicName}. We'd be glad to schedule a new time.`,
  },
  "owner.welcome": {
    he: ({ name }) => `היי ${name}, ברוכ/ה הבא/ה ל-CliniKit! אני ניב ואני כאן לכל שאלה. יש לך 30 ימי ניסיון. התחלה מהירה: clinikit.co.il/dashboard`,
    en: ({ name }) => `Hi ${name}, welcome to CliniKit! I'm Niv, here for any question. You have a 30-day trial. Quick start: clinikit.co.il/dashboard`,
  },
  "owner.trial7": {
    he: ({ name }) => `היי ${name}, נשארו 7 ימים לתקופת הניסיון ב-CliniKit. רוצה שנעבור יחד על מה שמתאים לקליניקה שלך? השב/י "כן" ואחזור אליך.`,
    en: ({ name }) => `Hi ${name}, 7 days left in your CliniKit trial. Want to go over what fits your clinic? Reply "yes" and I'll get back to you.`,
  },
  "owner.trial1": {
    he: ({ name }) => `היי ${name}, מחר מסתיימת תקופת הניסיון ב-CliniKit. כדי להמשיך בלי הפסקה: clinikit.co.il/dashboard/admin/settings`,
    en: ({ name }) => `Hi ${name}, your CliniKit trial ends tomorrow. To continue without interruption: clinikit.co.il/dashboard/admin/settings`,
  },
  "owner.trialExpired": {
    he: ({ name }) => `היי ${name}, תקופת הניסיון הסתיימה והנתונים שלך שמורים. אפשר להפעיל מנוי בכל רגע: clinikit.co.il/dashboard/admin/settings`,
    en: ({ name }) => `Hi ${name}, your trial has ended and your data is safe. You can activate a plan any time: clinikit.co.il/dashboard/admin/settings`,
  },
  "owner.planReverted": {
    he: ({ name }) => `היי ${name}, המנוי שלך ב-CliniKit הסתיים והקליניקה חזרה למצב ניסיון. לחידוש: clinikit.co.il/dashboard/admin/settings`,
    en: ({ name }) => `Hi ${name}, your CliniKit plan has ended and the clinic is back on trial. To renew: clinikit.co.il/dashboard/admin/settings`,
  },
  "lead.followup1": {
    he: ({ name }) => `היי ${name}, ניב מ-CliniKit 😊 ניסיתי להשיג אותך לגבי המערכת לניהול הקליניקה. מתי נוח לשיחה קצרה? (להסרה: הסר)`,
    en: ({ name }) => `Hi ${name}, Niv from CliniKit 😊 I tried reaching you about the clinic management system. When is a good time for a short call? (reply STOP to opt out)`,
  },
  "lead.followup2": {
    he: ({ name }) => `היי ${name}, אחרון לפני שאני מפסיק להציק 🙂 אם עדיין רלוונטי, 30 יום ניסיון חינם: clinikit.co.il/signup (להסרה: הסר)`,
    en: ({ name }) => `Hi ${name}, last one before I stop bugging you 🙂 If still relevant, 30 days free: clinikit.co.il/signup (reply STOP to opt out)`,
  },
  "prospect.approved": {
    he: ({ name }) => `היי ${name}, כאן ניב מ-CliniKit 😊 אישרנו לך הרשמה למערכת. להשלמת ההרשמה: clinikit.co.il/signup`,
    en: ({ name }) => `Hi ${name}, Niv from CliniKit 😊 your registration was approved. Complete it here: clinikit.co.il/signup`,
  },
  "prospect.regNudge": {
    he: ({ name }) => `היי ${name}, ראיתי שעדיין לא השלמת את ההרשמה ל-CliniKit. אשמח לעזור אם משהו לא ברור! clinikit.co.il/signup`,
    en: ({ name }) => `Hi ${name}, I noticed you haven't completed your CliniKit registration yet. Happy to help if anything is unclear! clinikit.co.il/signup`,
  },

  "payment.reminder": {
    fallbacks: [(v) => ({ ...v, clinicName: "" })],
    he: ({ name, clinicName, date, time }) => {
      const c = clinicName ? ` ב${clinicName}` : "";
      return `שלום ${name}, תזכורת ידידותית: נותר תשלום שטרם הוסדר עבור הפגישה שלך${c} בתאריך ${date} בשעה ${time}. נשמח שתסדיר/י את התשלום בהקדם. תודה רבה!`;
    },
    en: ({ name, clinicName, date, time }) => {
      const c = clinicName ? ` at ${clinicName}` : "";
      return `Hello ${name}, a friendly reminder: there is still an outstanding payment for your appointment${c} on ${date} at ${time}. We would appreciate it if you could settle it soon. Thank you!`;
    },
  },
  // Multi-clinic v2 — team-member invite (system SMS, CliniKit's cost, not the
  // clinic quota). The login link is the full URL (phone-OTP needs no token).
  // Optional admin note is the first thing dropped when the message overflows.
  "team.invite": {
    fallbacks: [(v) => ({ ...v, note: "" })],
    he: ({ firstName, adminName, clinicName, roleLabel, note }) => {
      const who = adminName ? `${adminName} הוסיף/ה אותך` : "הוספנו אותך";
      const where = clinicName ? ` לצוות ${clinicName}` : " לצוות הקליניקה";
      const role = roleLabel ? ` כ${roleLabel}` : "";
      const extra = note ? ` ${note}` : "";
      return `שלום ${firstName}, ${who}${where} ב-CliniKit${role}.${extra}\nכניסה עם מספר הטלפון הזה:\nhttps://clinikit.co.il/login`;
    },
    en: ({ firstName, adminName, clinicName, roleLabel, note }) => {
      const who = adminName ? `${adminName} added you` : "You were added";
      const where = clinicName ? ` to the ${clinicName} team` : " to the clinic team";
      const role = roleLabel ? ` as ${roleLabel}` : "";
      const extra = note ? ` ${note}` : "";
      return `Hi ${firstName}, ${who}${where} on CliniKit${role}.${extra}\nSign in with this phone number:\nhttps://clinikit.co.il/login`;
    },
  },
};

export const TEMPLATE_IDS = Object.keys(TEMPLATES);

/**
 * Placeholder-style templates. Unlike the entries above these are stored as
 * strings (on the clinic doc, or passed in by the therapist) and rendered by
 * `{placeholder}` substitution, so they cannot be functions.
 *
 * A therapist who customised the text gets it sent verbatim in any language —
 * translating someone's own wording is out of scope by product decision.
 */
export const PLACEHOLDER_TEMPLATES = {
  "payment.summary.default": {
    he: 'היי {name}, סיכום תשלומים ממתינים עבור הטיפולים בתאריכים: {dates}. סה"כ לתשלום: {amount} ש"ח. נא ליצור קשר עם הקליניקה להסדרת התשלום, תודה!',
    en: "Hi {name}, a summary of pending payments for treatments on: {dates}. Total due: {amount} ILS. Please contact the clinic to arrange payment, thank you!",
  },
  "questionnaire.request": {
    he: 'שלום {name}, נשלח אליך שאלון "{questionnaireName}" למילוי.\nלמילוי לחץ/י כאן:\n{link}',
    en: 'Hello {name}, the questionnaire "{questionnaireName}" has been sent to you.\nClick here to fill it in:\n{link}',
  },
  // Grow payments (grow_integration.md Phase 6) — {link} = clinikit.co.il/pay/<code>
  "grow.paymentRequest": {
    he: "שלום {name}, {clinicName} שלחה לך דרישת תשלום על סך {amount} ₪ {descriptionPart}.\nלתשלום מאובטח (אשראי / Bit / Apple Pay / Google Pay):\n{link}",
    en: "Hello {name}, {clinicName} sent you a payment request of {amount} ILS {descriptionPart}.\nSecure payment (card / Bit / Apple Pay / Google Pay):\n{link}",
  },
  "grow.paymentReminder": {
    he: "שלום {name}, תזכורת: דרישת התשלום מ{clinicName} על סך {amount} ₪ עדיין פתוחה.\nלתשלום מאובטח:\n{link}",
    en: "Hello {name}, a reminder: the payment request from {clinicName} for {amount} ILS is still open.\nSecure payment:\n{link}",
  },
  "grow.paymentReceipt": {
    he: "שלום {name}, התשלום ל{clinicName} על סך {amount} ₪ התקבל, תודה!\nהקבלה שלך:\n{link}",
    en: "Hello {name}, your payment of {amount} ILS to {clinicName} was received, thank you!\nYour receipt:\n{link}",
  },
  "signature.request": {
    he: 'שלום {name}, נשלח אליך טופס "{formName}" לחתימה דיגיטלית.\nלחתימה לחץ/י כאן:\n{link}',
    en: 'Hello {name}, the form "{formName}" has been sent to you for digital signature.\nClick here to sign:\n{link}',
  },
  // Nutrition clinics — the coach's one-shot "time to measure" nudge
  "nutrition.measurementReminder": {
    he: "שלום {name}, הגיע הזמן למדידה השבועית. אפשר להזין אותה בעמוד האישי:\n{link}",
    en: "Hello {name}, it's time for your measurement. You can add it on your personal page:\n{link}",
  },
  // Nutrition clinics — the client's personal page (menu, food log, measurements)
  "nutrition.menuLink": {
    he: "שלום {name}, העמוד האישי שלך מוכן — תפריט, דיווח אכילה ומדידות.\nלכניסה לחץ/י כאן:\n{link}",
    en: "Hello {name}, your personal page is ready — menu, food log and measurements.\nTap here to open it:\n{link}",
  },
};

/** The stored/placeholder template for an id, defaulting to Hebrew. */
export const getPlaceholderTemplate = (id, language) => {
  const entry = PLACEHOLDER_TEMPLATES[id];
  if (!entry) throw new Error(`[CK-9761] Unknown placeholder template: ${id}`);
  return entry[normLang(language)];
};

/**
 * Render a catalog template.
 *
 * @param {string} id template id from TEMPLATES
 * @param {"he"|"en"|undefined} language absent/unknown → Hebrew
 * @param {object} vars template variables
 * @param {object} [opts]
 *   link       {string} appended as "\n\n<link>" and counted against the cap
 *   linkChars  {number} length budget to reserve when there is no real link yet
 *                       (preview callers pass SMS_LINK_CHARS)
 *   suffix     {string} appended AFTER truncation — must survive intact
 *   max        {number} character cap (default MAX_SMS_LENGTH)
 *   truncate   {boolean} apply truncateMessage (default true; previews pass false)
 *   hasLink    {boolean} override the closing-clause choice when the link is
 *                        appended downstream rather than here
 * @returns {{ message: string, omitted: string[] }} `omitted` lists the vars a
 *   fallback stripped, so the UI can tell the therapist what was dropped.
 */
export const renderSms = (
  id,
  language,
  vars,
  {
    link = "",
    linkChars = 0,
    suffix = "",
    max = MAX_SMS_LENGTH,
    truncate = true,
    hasLink,
  } = {},
) => {
  const lang = normLang(language);
  const tpl = TEMPLATES[id];
  if (!tpl) throw new Error(`[CK-9761] Unknown SMS template: ${id}`);

  // `hasLink` normally follows `link`, but a caller that hands the rendered text
  // to someone else who appends the URL (the series SMS → sendAppointmentSMS)
  // sets it explicitly so the closing clause still reads as link-bearing.
  const ctx = { hasLink: hasLink === undefined ? !!link : hasLink };
  const tail = link ? `\n\n${link}` : "";
  const budget = link ? tail.length : linkChars;

  let v = vars;
  let body = tpl[lang](v, ctx);
  const omitted = [];

  // Record only vars a fallback actually changed — stripping an already-empty
  // var must not claim anything was omitted.
  const recordOmissions = (before, after) => {
    Object.keys(after).forEach((k) => {
      if (after[k] !== before[k] && !omitted.includes(k)) omitted.push(k);
    });
  };

  const fallbacks = tpl.fallbacks || [];
  for (let i = 0; i < fallbacks.length; i += 1) {
    if (body.length + budget <= max) break;
    const next = fallbacks[i](v);
    recordOmissions(v, next);
    v = next;
    body = tpl[lang](v, ctx);
  }

  const message = truncate ? truncateMessage(body + tail, max) : body + tail;
  return { message: message + suffix, omitted };
};

/** Convenience wrapper for callers that only want the string. */
export const renderSmsText = (id, language, vars, opts) =>
  renderSms(id, language, vars, opts).message;
